import { useMutation, useQueryClient } from '@tanstack/react-query';

import queryKeys from '@pray/shared/queryKeys';
import api from '@pray/shared/utils/api';

/** 1 hold payouts · 2 suspend giving & campaigns · 3 hide from app · 4 remove permanently. */
export type DeactivationLevel = 1 | 2 | 3 | 4;

export type ReasonCode = 'churned' | 'non_payment' | 'contract_ended' | 'rights_or_conduct' | 'other';

export type SurfaceOutcome = {
  surface: string;
  targetId: string;
  status: string;
  error: string | null;
};

/**
 * One result per artist. The backend applies each account independently, so a bulk request can
 * come back part applied and part error — the UI must show every row, not a single verdict.
 */
export type DeactivationResult = {
  artistId: string;
  status: 'applied' | 'partial' | 'error' | string;
  deactivationId?: string;
  surfaces?: SurfaceOutcome[];
  error?: string;
};

type DeactivateAccountsParams = {
  artistIds: string[];
  level: DeactivationLevel;
  reasonCode: ReasonCode;
  reasonNote: string;
  /** The separate opt-in to move each account's recurring gifts onto Pray's account. Level 3 only. */
  moveRecurringGifts?: boolean;
};

export async function deactivateAccounts({
  artistIds,
  level,
  reasonCode,
  reasonNote,
  moveRecurringGifts = false,
}: DeactivateAccountsParams): Promise<DeactivationResult[]> {
  if (!artistIds?.length) throw Error('At least one account is required');
  if (!reasonNote?.trim()) throw Error('A reason note is required');

  const url = '/web/studio/admin/account-status';
  const response = await api.executePostRequest(url, {
    artistIds,
    level,
    reasonCode,
    reasonNote: reasonNote.trim(),
    // Sent only when ticked: the server defaults it off, and refuses it at any level but 3.
    ...(moveRecurringGifts ? { moveRecurringGifts: true } : {}),
  });

  // List envelope: { object: 'list', data_count, data: DeactivationResult[] }
  return response.data.data;
}

export async function reinstateAccounts({ artistIds }: { artistIds: string[] }): Promise<DeactivationResult[]> {
  if (!artistIds?.length) throw Error('At least one account is required');

  const url = '/web/studio/admin/account-status/reinstate';
  const response = await api.executePostRequest(url, { artistIds });

  return response.data.data;
}

export function useDeactivateAccounts() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: deactivateAccounts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accountStatus() });
    },
  });

  const { mutateAsync, ...rest } = mutation;
  return { deactivateAccounts: mutateAsync, ...rest };
}

export function useReinstateAccounts() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: reinstateAccounts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.accountStatus() });
    },
  });

  const { mutateAsync, ...rest } = mutation;
  return { reinstateAccounts: mutateAsync, ...rest };
}
